import { useList } from '@refinedev/core';
import { useMemo } from 'react';
import { Key, Loader2, ArrowUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TableToolbar } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { Permission } from '../roles/types';

type SortField = 'name' | 'code';
type SortOrder = 'asc' | 'desc';

/**
 * Permissions list page (read-only, grouped by category)
 */
export function PermissionsList() {
  useScrollRestoration();
  const [search, setSearch] = usePersistedState('permissions:search', '');
  const [sortField, setSortField] = usePersistedState<SortField>('permissions:sortField', 'name');
  const [sortOrder, setSortOrder] = usePersistedState<SortOrder>('permissions:sortOrder', 'asc');
  const pageSize = 200;

  const listResult = useList<Permission>({
    resource: 'permissions',
    pagination: { currentPage: 1, pageSize },
    filters: search
      ? [{ field: 'search', operator: 'contains', value: search }]
      : [],
  });

  const rawPermissions = listResult.result?.data;
  const isLoading = listResult.query?.isLoading ?? false;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const groupedPermissions = useMemo(() => {
    const groups: Record<string, Permission[]> = {};
    for (const perm of rawPermissions ?? []) {
      const category = perm.category || 'other';
      if (!groups[category]) { groups[category] = []; }
      groups[category].push(perm);
    }
    // Sort items within each category
    for (const category of Object.keys(groups)) {
      groups[category] = [...groups[category]].sort((a, b) => {
        const aVal = sortField === 'name' ? a.name : a.code;
        const bVal = sortField === 'name' ? b.name : b.code;
        const cmp = aVal.localeCompare(bVal, 'ru');
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }
    return groups;
  }, [rawPermissions, sortField, sortOrder]);

  // Sort categories alphabetically
  const sortedCategories = useMemo(
    () => Object.keys(groupedPermissions).sort((a, b) => a.localeCompare(b, 'ru')),
    [groupedPermissions],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader
        title="Права доступа"
        description={`Список всех доступных прав в системе (${rawPermissions?.length ?? 0})`}
        sticky
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <TableToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Поиск по ключу..."
          />
        </div>

        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <Button
            variant={sortField === 'name' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => toggleSort('name')}
            className="gap-1.5"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            По названию
            {sortField === 'name' && (
              <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
            )}
          </Button>
          <Button
            variant={sortField === 'code' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => toggleSort('code')}
            className="gap-1.5"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            По коду
            {sortField === 'code' && (
              <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
            )}
          </Button>
        </div>
      </div>

      {/* Permissions by category */}
      {sortedCategories.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search ? 'Ничего не найдено' : 'Нет прав в системе'}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sortedCategories.map((category) => (
            <Card key={category}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Key className="h-4 w-4 text-muted-foreground" />
                  {groupedPermissions[category]?.[0]?.categoryLabel ??
                    category.charAt(0).toUpperCase() + category.slice(1)}
                  <Badge variant="secondary" className="ml-auto">
                    {groupedPermissions[category].length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {groupedPermissions[category].map((perm: Permission) => (
                    <div
                      key={perm.code}
                      className="flex flex-col gap-1 p-2 rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">
                          {perm.name}
                        </span>
                      </div>
                      <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded w-fit">
                        {perm.code}
                      </code>
                      {perm.description ? (
                        <p className="text-xs text-muted-foreground">
                          {perm.description}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {perm.name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
